import apiClient from '../../../services/api-client'
import type {
  TenantRequest,
  SubmitTenantRequestRequest,
  ReviewRequest,
  TenantRequestListResponse,
  TenantRequestStatus,
} from '../types/tenant-request.types'

/**
 * Submit a new tenant request (Zealys employees only)
 */
export async function submitTenantRequest(request: SubmitTenantRequestRequest): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>('/tenant-request', request)
  return response.data
}

/**
 * Update a pending tenant request (Requester or Approver)
 */
export async function updateTenantRequest(id: string, request: SubmitTenantRequestRequest): Promise<void> {
  await apiClient.put(`/tenant-request/${id}`, request)
}

/**
 * Get all tenant requests (SysAdmin or Workspace Admin)
 */
export async function getAllTenantRequests(status?: TenantRequestStatus): Promise<TenantRequest[]> {
  const response = await apiClient.get<TenantRequestListResponse>('/tenant-request', {
    params: status ? { status } : undefined,
  })
  return response.data.data
}

/**
 * Get tenant requests submitted by current user (Zealys employees)
 */
export async function getMyTenantRequests(): Promise<TenantRequest[]> {
  const response = await apiClient.get<TenantRequestListResponse>('/tenant-request/my-requests')
  return response.data.data
}

/**
 * Get a specific tenant request by ID
 */
export async function getTenantRequestById(id: string): Promise<TenantRequest> {
  const response = await apiClient.get<TenantRequest>(`/tenant-request/${id}`)
  return response.data
}

/**
 * Approve a tenant request (SysAdmin or Workspace Admin)
 */
export async function approveTenantRequest(id: string, review: ReviewRequest): Promise<void> {
  await apiClient.post(`/tenant-request/${id}/approve`, review)
}

/**
 * Reject a tenant request (SysAdmin or Workspace Admin)
 */
export async function rejectTenantRequest(id: string, review: ReviewRequest): Promise<void> {
  await apiClient.post(`/tenant-request/${id}/reject`, review)
}

/**
 * Mark request as completed after manual tenant creation (SysAdmin or Workspace Admin)
 */
export async function markTenantRequestCompleted(id: string): Promise<void> {
  await apiClient.post(`/tenant-request/${id}/complete`)
}

/**
 * Get allowed workspace types for the currently authenticated user
 */
export async function getAllowedTypes(): Promise<string[]> {
  const response = await apiClient.get<{ types: string[] }>('/tenant-request/allowed-types')
  return response.data.types
}
